import { sortSizesFromStrings } from '@/lib/helpers/sort-size.helper'
import { stringToSlug } from '@/lib/utils'
import { Product, ProductVariant } from '@/types/product'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface UseProductSizesProps {
  product: Product
  colorSelected: string | null
  selectedSize: string | null
  onSizeSelect: (size: string) => void
}

export const useProductSizes = ({
  product,
  colorSelected,
  selectedSize,
  onSizeSelect,
}: UseProductSizesProps) => {
  const [sizeSelected, setSizeSelected] = useState<string | null>(selectedSize)

  // Extract size options from product variants based on selected color
  const sizeOptions = useMemo((): string[] => {
    if (!colorSelected || !product.variants) {
      return []
    }

    // Find variants that match the selected color (option1 is typically color)
    const variantsForColor = product.variants.filter(
      (variant: ProductVariant) => {
        if (variant.hide || !variant.available) return false
        return stringToSlug(variant.option1) === colorSelected
      }
    )

    // Extract unique sizes (option2 is typically size)
    const sizes = variantsForColor
      .map((variant: ProductVariant) => variant.option2)
      .filter((size: string) => size && size.trim() !== '')
      .filter(
        (size: string, index: number, array: string[]) =>
          array.indexOf(size) === index
      )

    return sortSizesFromStrings(sizes)
  }, [colorSelected, product.variants])

  const isDisabled = useCallback(
    (size: string): boolean => {
      if (!colorSelected || !product.variants) {
        return true
      }

      const variantWithSize = product.variants.find(
        (variant: ProductVariant) => {
          const hasMinimumQuantity = variant.product_apply_allowance_inventory
            ? variant.quantity > 5
            : variant.quantity > 0

          return (
            stringToSlug(variant.option1) === colorSelected &&
            variant.option2 === size &&
            !variant.hide &&
            variant.available &&
            hasMinimumQuantity
          )
        }
      )

      return !variantWithSize
    },
    [colorSelected, product.variants]
  )

  // Update selected size when color changes
  useEffect(() => {
    if (!colorSelected || !product.variants) return

    const isSizeValid = sizeSelected && !isDisabled(sizeSelected)

    if (!isSizeValid) {
      const firstValidSize = sizeOptions.find((size) => !isDisabled(size))
      if (firstValidSize) {
        setSizeSelected(firstValidSize)
        onSizeSelect(firstValidSize)
      }
    }
  }, [
    colorSelected,
    sizeSelected,
    sizeOptions,
    isDisabled,
    product.variants,
    onSizeSelect,
  ])

  // Sync with external selectedSize prop
  useEffect(() => {
    setSizeSelected(selectedSize)
  }, [selectedSize])

  const handleSizeClick = (sizeOption: string) => {
    if (!isDisabled(sizeOption)) {
      setSizeSelected(sizeOption)
      onSizeSelect(sizeOption)
    }
  }

  return {
    sizeOptions,
    sizeSelected,
    isDisabled,
    handleSizeClick,
  }
}
